import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Sparkles,
  X,
  Send,
  Loader2,
  MoreHorizontal,
  Stethoscope,
  Wrench,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  HelpCircle,
  MessageCircle,
  Eraser,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToBalanceInvalidation } from '../hooks/useAccountBalances';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fin AI — the financial command center panel.
 *
 * Not a chatbot widget — a system-aware assistant that opens with:
 *   1. Live System Status (green / yellow / red / unset)
 *   2. Quick actions (Diagnose / Fix / Optimize) — auto-send smart prompts
 *   3. System-aware prompt chips — preload the composer
 *   4. Clean transcript (only shown once the conversation starts)
 *   5. Sticky composer
 *
 * Backend contracts preserved (no changes to /api/ai/chat or /history).
 * System status derived from existing endpoints:
 *   - /api/system/health
 *   - /api/settings/rebalance-suggestions
 *   - /api/settings/required-weekly-income
 */

const STATUS_META = {
  green: {
    Icon: CheckCircle2,
    badge: 'System aligned',
    tone: 'emerald',
    wrap: 'bg-emerald-500/10 border-emerald-500/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-400',
  },
  yellow: {
    Icon: AlertTriangle,
    badge: 'Needs attention',
    tone: 'amber',
    wrap: 'bg-amber-500/10 border-amber-500/30',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-400',
  },
  red: {
    Icon: AlertOctagon,
    badge: 'Off track',
    tone: 'rose',
    wrap: 'bg-rose-500/10 border-rose-500/30',
    text: 'text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-400',
  },
  unset: {
    Icon: HelpCircle,
    badge: 'Not initialized',
    tone: 'gray',
    wrap: 'bg-gray-500/10 border-gray-400/30',
    text: 'text-gray-700 dark:text-gray-200',
    dot: 'bg-gray-400',
  },
};

function deriveStatus({ health, pace, rebalance }) {
  if (!health || health.status === 'unset') {
    return {
      level: 'unset',
      title: 'System not initialized',
      message: "You haven't fully set up your financial system yet.",
      issue: null,
      guide: null,
      suggestions: ['Set up my plan', 'Add my income', 'Start tracking'],
    };
  }

  const paceStatus = pace?.income_pace?.status; // 'ahead' | 'on_pace' | 'behind'
  const behindAmount = Number(pace?.income_pace?.behind_amount || 0);
  const aheadAmount = Number(pace?.income_pace?.ahead_amount || 0);
  const totalDeficit = Number(rebalance?.total_deficit || 0);
  const aligned = health.status === 'aligned';
  const offenders = Array.isArray(health.offenders) ? health.offenders : [];

  const biggestOffender = (() => {
    if (!rebalance?.account_health) return null;
    let worst = null;
    let worstMag = 0;
    Object.entries(rebalance.account_health).forEach(([name, h]) => {
      const diff = Math.abs(Number(h.difference || 0));
      if (diff > worstMag) { worst = name; worstMag = diff; }
    });
    return worst;
  })();

  // RED: income is behind AND accounts are materially drifted
  if (paceStatus === 'behind' && !aligned && totalDeficit > 0) {
    return {
      level: 'red',
      title: 'System off track',
      message: "Your current income and spending are not supporting your system requirements.",
      issue: behindAmount > 0
        ? `You're $${behindAmount.toLocaleString()} behind your required income pace`
        : 'Multiple accounts are significantly off target',
      guide: 'If this continues, your system will become unstable.',
      suggestions: ['What should I fix first?', 'Create a recovery plan', 'How much do I need to earn?'],
    };
  }

  // YELLOW: income ok, but accounts drifted
  if (!aligned) {
    const issue = offenders.length > 1
      ? `Multiple accounts have drifted from target: ${offenders.join(', ')}`
      : biggestOffender
        ? `Biggest issue: ${biggestOffender.charAt(0).toUpperCase()+biggestOffender.slice(1)} account is out of alignment`
        : 'Accounts have drifted from their targets';
    return {
      level: 'yellow',
      title: 'System needs attention',
      message: 'Your income is solid, but your accounts are out of alignment with your plan.',
      issue,
      guide: "This doesn't break your system — but it does reduce efficiency.",
      suggestions: ['Show me what to fix', 'Review rebalance plan', 'Fix this for me'],
    };
  }

  // GREEN: aligned
  return {
    level: 'green',
    title: 'System running smoothly',
    message: 'Your system is on track. Income, expenses, and allocations are all aligned.',
    issue: null,
    guide: aheadAmount > 0
      ? `You're currently $${aheadAmount.toLocaleString()} ahead of your required pace — keep this up.`
      : "You're on pace — keep this up.",
    suggestions: ['How do I get further ahead?', 'Where can I optimize next?', 'Increase my savings strategy'],
  };
}

const PROMPT_CHIPS = [
  'Show why my system is off track',
  'What should I fix first?',
  'Break down my spending this month',
  'How much can I safely spend?',
  'How do I get ahead faster?',
  'Explain my rebalance plan',
  "What changed this month?",
  "What's hurting my system most?",
];

// Frontend mirror of backend is_diagnostic_intent — used to tag outgoing
// messages so we can render contextual CTA chips beneath the reply.
const DIAGNOSTIC_INTENT_PATTERNS = [
  'show me what to fix',
  'what to fix first',
  'what should i fix',
  "what is wrong",
  "what's wrong",
  "whats wrong",
  "what's hurting",
  "whats hurting",
  'what is hurting',
  'what needs attention',
  'fix this for me',
  'diagnose',
  'why is my system off',
  'why my system is off',
  'show why my system is off',
  "what's breaking",
  'whats breaking',
];

const isDiagnosticIntent = (text) => {
  if (!text) return false;
  const m = text.toLowerCase();
  return DIAGNOSTIC_INTENT_PATTERNS.some((p) => m.includes(p));
};

// Build up to 3 contextual CTA chips for a diagnostic reply, ranked the
// same way the backend ranks issues (drift → rebalance → pace → uncategorized → plan).
// Primary chip ties to the top recommended_action (usually the ACTION that
// fixes the issue, e.g. "Review Rebalance Plan" — not just a read-only page).
function buildDiagnosticCtas({ health, pace, rebalance }) {
  const ctas = [];
  const drifted = Array.isArray(health?.offenders) ? health.offenders : [];
  const totalDeficit = Number(rebalance?.total_deficit || 0);
  const paceStatus = pace?.income_pace?.status;
  const needsAttention = health?.status === 'needs_attention';

  // Primary: the action that resolves the top issue.
  if (totalDeficit > 1) {
    ctas.push({ label: 'Review Rebalance Plan', route: '__rebalance__' });
  } else if (drifted.length > 0 || needsAttention) {
    ctas.push({ label: 'Open My Balances', route: '/settings?tab=balances' });
  }

  // Secondary: the related reconciliation / visibility surface.
  if (totalDeficit > 1 && (drifted.length > 0 || needsAttention)) {
    ctas.push({ label: 'Open My Balances', route: '/settings?tab=balances' });
  }
  if (paceStatus === 'behind') {
    ctas.push({ label: 'View Reports', route: '/reports' });
  }

  // Tertiary: data hygiene fallback.
  if (ctas.length < 3) {
    ctas.push({ label: 'Open Expenses', route: '/expenses' });
  }
  return ctas.slice(0, 3);
}

function StatusCard({ status, onSuggestion }) {
  const meta = STATUS_META[status.level];
  const { Icon } = meta;
  return (
    <div
      data-testid="fin-ai-status-card"
      data-status-level={status.level}
      className={`rounded-xl border ${meta.wrap} px-4 py-3`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-medium ${meta.text} bg-white/30 dark:bg-black/20`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
          {meta.badge}
        </span>
        <Icon className={`w-3.5 h-3.5 ${meta.text}`} aria-hidden="true" />
      </div>
      <h3 className={`mt-1.5 text-sm font-semibold ${meta.text}`} data-testid="fin-ai-status-title">
        {status.title}
      </h3>
      <p className="mt-0.5 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
        {status.message}
      </p>
      {status.issue && (
        <p data-testid="fin-ai-status-issue" className={`mt-1 text-xs font-medium ${meta.text}`}>
          {status.issue}
        </p>
      )}
      {status.guide && (
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
          {status.guide}
        </p>
      )}
      {status.suggestions?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="fin-ai-status-suggestions">
          {status.suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSuggestion(s)}
              className={`px-2 py-1 rounded-md text-[11px] font-medium bg-white/50 dark:bg-white/5 border border-white/40 dark:border-white/10 ${meta.text} hover:bg-white/70 dark:hover:bg-white/10 transition-colors`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const QUICK_ACTIONS = [
  {
    id: 'diagnose',
    label: 'Diagnose System',
    Icon: Stethoscope,
    prompt: 'Diagnose my financial system right now. What is helping and what is hurting it? Use my live income pace, account balances, and expenses.',
  },
  {
    id: 'fix',
    label: 'Fix Imbalance',
    Icon: Wrench,
    prompt: 'What is the fastest way to bring my accounts back into alignment given my current balances and minimum balance protections? Be specific and prioritized.',
  },
  {
    id: 'optimize',
    label: 'Optimize Plan',
    Icon: TrendingUp,
    prompt: 'Based on my current data, what are the top 3 ways I can improve my financial setup? Focus on high-leverage changes.',
  },
];

function QuickActionRow({ onAction, disabled }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="fin-ai-quick-actions">
      {QUICK_ACTIONS.map(({ id, label, Icon, prompt }) => (
        <button
          key={id}
          type="button"
          disabled={disabled}
          onClick={() => onAction(prompt)}
          data-testid={`fin-ai-quick-${id}`}
          className="inline-flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <span className="text-[11px] font-medium text-gray-800 dark:text-gray-100 leading-tight text-center">
            {label}
          </span>
        </button>
      ))}
    </div>
  );
}

function PromptChips({ onPick, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="fin-ai-prompt-chips">
      {PROMPT_CHIPS.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          onClick={() => onPick(p)}
          className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function FinAI() {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  // Live system state
  const [health, setHealth] = useState(null);
  const [pace, setPace] = useState(null);
  const [rebalance, setRebalance] = useState(null);
  const [contextLoaded, setContextLoaded] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const loadContext = useCallback(async () => {
    try {
      const [h, p, r] = await Promise.all([
        axios.get(`${API}/system/health`, { headers: authHeaders }).then((x) => x.data).catch(() => null),
        axios.get(`${API}/settings/required-weekly-income`, { headers: authHeaders }).then((x) => x.data).catch(() => null),
        axios.get(`${API}/settings/rebalance-suggestions`, { headers: authHeaders }).then((x) => x.data).catch(() => null),
      ]);
      setHealth(h);
      setPace(p);
      setRebalance(r);
    } finally {
      setContextLoaded(true);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isOpen) loadContext();
  }, [isOpen, loadContext]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Re-pull context when any balance mutation invalidates the shared cache.
    const unsub = subscribeToBalanceInvalidation(loadContext);
    return () => unsub?.();
  }, [isOpen, loadContext]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const status = useMemo(
    () => deriveStatus({ health, pace, rebalance }),
    [health, pace, rebalance]
  );

  const abortRef = useRef(null);

  const sendRaw = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMessage = text.trim();
    const diagnostic = isDiagnosticIntent(userMessage);
    setMessages((prev) => [
      ...prev,
      { type: 'user', content: userMessage, ts: new Date().toISOString(), diagnostic },
    ]);
    setInput('');
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { message: userMessage, session_id: sessionId },
        { headers: authHeaders, signal: controller.signal }
      );
      if (!sessionId && res.data?.session_id) setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, {
        type: 'ai',
        content: res.data?.response ?? '',
        ts: new Date().toISOString(),
        diagnostic,
      }]);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      setMessages((prev) => [...prev, {
        type: 'ai',
        content: "I couldn't reach the assistant just now. Please try again in a moment.",
        ts: new Date().toISOString(),
        error: true,
      }]);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
    }
  }, [authHeaders, loading, sessionId]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    sendRaw(input);
  };

  const handleSuggestionOrAction = (text) => {
    // Status card & quick action buttons auto-send for an operator-like feel.
    sendRaw(text);
  };

  const handleChipPick = (text) => {
    // Prompt chips preload the composer so the user can tweak before sending.
    setInput(text);
    inputRef.current?.focus();
  };

  const clearChat = async () => {
    setOverflowOpen(false);
    // Cancel any in-flight AI request so the status card reappears cleanly.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (abortErr) {
        // Non-fatal: AbortController may already be settled. Log in dev so
        // surprising aborts are still visible without breaking clearChat.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[FinAI] abortRef.abort() ignored:', abortErr);
        }
      }
      abortRef.current = null;
    }
    setLoading(false);
    try {
      if (sessionId) {
        await axios.delete(`${API}/ai/chat/history?session_id=${sessionId}`, { headers: authHeaders });
      }
    } catch (historyErr) {
      // Non-blocking: still reset local transcript even if history delete
      // failed (network hiccup, expired session, etc.). Surface in dev.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[FinAI] clearChat history delete failed:', historyErr);
      }
    }
    setMessages([]);
    setSessionId(null);
  };

  const handleCta = useCallback((cta) => {
    if (cta.route === '__rebalance__') {
      window.location.href = '/?open=rebalance';
      return;
    }
    if (cta.route?.startsWith('/')) {
      window.location.href = cta.route;
    }
  }, []);

  const diagnosticCtas = useMemo(
    () => buildDiagnosticCtas({ health, pace, rebalance }),
    [health, pace, rebalance]
  );

  const hasTranscript = messages.length > 0 || loading;

  return (
    <>
      {/* Floating toggle */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
        data-testid="fin-ai-toggle"
        aria-label={isOpen ? 'Close Fin AI' : 'Open Fin AI'}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <div className="relative">
            <MessageCircle className="w-6 h-6" />
            <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-300" />
          </div>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden
            bottom-24 right-4 left-4 rounded-2xl
            sm:left-auto sm:right-6 sm:w-[420px] sm:max-w-[calc(100vw-3rem)]"
          style={{ height: 'min(640px, calc(100vh - 140px))' }}
          data-testid="fin-ai-chat-window"
          role="dialog"
          aria-label="Fin AI financial command center"
        >
          {/* -------------------- Header -------------------- */}
          <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-800">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-sm">
                  <Sparkles className="w-3.5 h-3.5 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-testid="fin-ai-title">
                  Fin AI
                </h3>
              </div>
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                Your financial command center
              </p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-snug">
                System-aware assistant • Connected to your income, expenses & accounts
              </p>
            </div>

            {/* De-emphasized overflow menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOverflowOpen((v) => !v)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="More options"
                data-testid="fin-ai-overflow-btn"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {overflowOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-40 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg z-10"
                  data-testid="fin-ai-overflow-menu"
                >
                  <button
                    type="button"
                    onClick={clearChat}
                    data-testid="fin-ai-clear-chat"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <Eraser className="w-3.5 h-3.5" aria-hidden="true" />
                    Clear conversation
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="ml-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Close"
                data-testid="fin-ai-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* -------------------- Scrollable body -------------------- */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {/* Status card + opening actions — hidden once a conversation starts */}
            {!hasTranscript && (
              <>
                {contextLoaded ? (
                  <StatusCard status={status} onSuggestion={handleSuggestionOrAction} />
                ) : (
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-6 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" aria-hidden="true" />
                    Reading your system…
                  </div>
                )}

                <QuickActionRow onAction={handleSuggestionOrAction} disabled={loading} />

                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                    Try asking
                  </p>
                  <PromptChips onPick={handleChipPick} disabled={loading} />
                </div>

                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 p-3">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-100">
                    Ask anything about your system
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed">
                    I already have your income, expenses, balances, and plan context. I can help you understand what&rsquo;s happening, what to fix, and what to do next.
                  </p>
                </div>
              </>
            )}

            {/* Transcript */}
            {hasTranscript && (
              <div className="space-y-3" data-testid="fin-ai-transcript">
                {messages.map((msg, idx) => {
                  const isLast = idx === messages.length - 1;
                  const showCtas =
                    isLast &&
                    !loading &&
                    msg.type === 'ai' &&
                    !msg.error &&
                    msg.diagnostic &&
                    diagnosticCtas.length > 0;
                  return (
                    <div key={`${msg.ts}-${idx}`}>
                      <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          data-testid={`fin-ai-msg-${msg.type}`}
                          className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                            msg.type === 'user'
                              ? 'bg-blue-600 text-white rounded-br-md'
                              : msg.error
                                ? 'bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 rounded-bl-md'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                      {showCtas && (
                        <div
                          data-testid="fin-ai-response-ctas"
                          className="mt-2 flex flex-wrap gap-1.5 pl-1"
                        >
                          {diagnosticCtas.map((cta, ci) => (
                            <button
                              key={cta.label}
                              type="button"
                              onClick={() => handleCta(cta)}
                              data-testid={`fin-ai-cta-${ci}`}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                ci === 0
                                  ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                            >
                              {cta.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {loading && (
                  <div className="flex justify-start" data-testid="fin-ai-thinking">
                    <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 dark:text-blue-400" aria-hidden="true" />
                        <span className="text-xs text-gray-600 dark:text-gray-300">Thinking…</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* -------------------- Sticky composer -------------------- */}
          <form
            onSubmit={handleSubmit}
            className="border-t border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-gray-900"
          >
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-1.5 px-1 hidden sm:block">
              Fin AI can explain, diagnose, and help fix your financial system.
            </p>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your income, balances, spending, or how to improve your system…"
                className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={loading}
                data-testid="fin-ai-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="fin-ai-send"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default FinAI;
